/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useEffect, useRef} from 'react'
import {useIntl} from 'react-intl'

import {Box} from '@chakra-ui/react'
import {
    provinceOptions,
    stateOptions
} from '@salesforce/retail-react-app/app/components/forms/state-province-options'

import {
    createFormattedValidationRules,
    getPhoneFieldConfig,
    getPostalCodeValidationRules
} from '@epmc-mach/accelarator/overrides/app/components/forms/address-validation-rules'
import {SHIPPING_COUNTRY_CODES} from '@epmc-mach/accelarator/overrides/app/constants'
import {msgf, useTranslationHelpers} from '@epmc-mach/accelarator/overrides/app/hooks'
import {useCustomerLocation} from '../../contexts/CustomerLocation'

/**
 * A React hook that provides the field definitions for an address form.
 * @param {Object} form - The object returned from `useForm`
 * @param {Object} form.control - The form control object
 * @param {Object} form.trigger - The trigger function for manual validation
 * @param {Object} form.formState.errors - An object containing field errors
 * @param {Object} form.formState.touchedFields - An object containing touched field states
 * @returns {Object} Field definitions for use in a form
 */
export default function useAddressFields({
    form: {
        watch,
        control,
        trigger,
        setValue,
        clearErrors,
        formState: {errors, touchedFields}
    },
    prefix = ''
}) {
    const {formatMessage} = useIntl()
    const {tFormLabels} = useTranslationHelpers()

    const {defaultCountryCode, hasDefaultCountryCodeLoaded} = useCustomerLocation()
    const isProgrammaticCountryChange = useRef(false)

    useEffect(() => {
        if (hasDefaultCountryCodeLoaded && defaultCountryCode) {
            isProgrammaticCountryChange.current = true
            setValue(`${prefix}countryCode`, defaultCountryCode)

            // Delay reset of the flag to allow watch to capture it
            const timer = setTimeout(() => {
                isProgrammaticCountryChange.current = false
            }, 100)

            return () => clearTimeout(timer)
        }
    }, [defaultCountryCode, hasDefaultCountryCodeLoaded, prefix, setValue])

    const countryCode = watch(`${prefix}countryCode`) || defaultCountryCode

    // Get phone configuration once for both label and rules
    const phoneConfig = getPhoneFieldConfig(countryCode, {
        required: tFormLabels?.fieldRequired,
        invalid: tFormLabels?.invalidFormat
    })

    useEffect(() => {
        if (clearErrors) {
            clearErrors(`${prefix}phone`)
        }
        if (trigger && touchedFields?.[`${prefix}phone`]) {
            trigger(`${prefix}phone`)
        }
    }, [countryCode, trigger, clearErrors, prefix, touchedFields])

    const fields = {
        firstName: {
            name: `${prefix}firstName`,
            label: tFormLabels?.firstName,
            defaultValue: '',
            type: 'text',
            autoComplete: 'given-name',
            rules: createFormattedValidationRules('firstName', {
                required: tFormLabels?.fieldRequired,
                invalid: tFormLabels?.invalidFormat,
                minLength: tFormLabels?.minLength,
                maxLength: tFormLabels?.maxLength
            }),
            error: errors[`${prefix}firstName`],
            control
        },
        lastName: {
            name: `${prefix}lastName`,
            label: tFormLabels?.lastName,
            defaultValue: '',
            type: 'text',
            autoComplete: 'family-name',
            rules: createFormattedValidationRules('lastName', {
                required: tFormLabels?.fieldRequired,
                invalid: tFormLabels?.invalidFormat,
                minLength: tFormLabels?.minLength,
                maxLength: tFormLabels?.maxLength
            }),
            error: errors[`${prefix}lastName`],
            control
        },
        phone: {
            name: `${prefix}phone`,
            label: phoneConfig.isOptional
                ? msgf(tFormLabels?.phoneNumber, [
                      <Box as="span" color="black" fontWeight="thin">
                          {tFormLabels?.phoneNumberNote}
                      </Box>
                  ])
                : tFormLabels?.phoneNumber,
            defaultValue: '',
            type: 'tel',
            autoComplete: 'tel',
            rules: {
                ...phoneConfig.rules,
                validate: (value) => {
                    const currentPhoneConfig = getPhoneFieldConfig(
                        watch(`${prefix}countryCode`) || defaultCountryCode,
                        formatMessage
                    )

                    if (!currentPhoneConfig.isOptional && (!value || value.trim() === '')) {
                        return tFormLabels?.fieldRequired
                    }

                    if (value && currentPhoneConfig.rules.pattern) {
                        if (!currentPhoneConfig.rules.pattern.value.test(value)) {
                            return currentPhoneConfig.rules.pattern.message
                        }
                    }

                    return true
                }
            },
            error: errors[`${prefix}phone`],
            inputProps: ({onChange}) => ({
                inputMode: 'numeric',
                onChange(evt) {
                    onChange(evt.target.value.replace(/\D/g, ''))
                }
            }),
            control
        },
        countryCode: {
            name: `${prefix}countryCode`,
            label: tFormLabels?.country,
            defaultValue: defaultCountryCode,
            type: 'select',
            options: SHIPPING_COUNTRY_CODES,
            rules: {required: tFormLabels?.fieldRequired},
            error: errors[`${prefix}countryCode`],
            control
        },
        address1: {
            name: `${prefix}address1`,
            label: msgf(tFormLabels?.street, [
                <Box as="span" color="black" fontWeight="thin">
                    {tFormLabels?.streetNote}
                </Box>
            ]),
            defaultValue: '',
            type: 'text',
            autoComplete: 'address-line1',
            rules: createFormattedValidationRules('address1', {
                required: tFormLabels?.fieldRequired,
                invalid: tFormLabels?.invalidFormat,
                minLength: tFormLabels?.minLength,
                maxLength: tFormLabels?.maxLength
            }),
            error: errors[`${prefix}address1`],
            control
        },
        address2: {
            name: `${prefix}address2`,
            label: tFormLabels?.housenumber,
            defaultValue: '',
            type: 'text',
            autoComplete: 'address-line2',
            rules: createFormattedValidationRules('address2', {
                required: tFormLabels?.fieldRequired,
                invalid: tFormLabels?.invalidFormat,
                minLength: tFormLabels?.minLength,
                maxLength: tFormLabels?.maxLength
            }),
            error: errors[`${prefix}address2`],
            control
        },
        c_address3: {
            name: `${prefix}c_address3`,
            label: msgf(tFormLabels?.addition, [
                <Box as="span" color="black" fontWeight="thin">
                    {tFormLabels?.additionNote}
                </Box>
            ]),
            defaultValue: '',
            type: 'text',
            autoComplete: 'suite',
            rules: createFormattedValidationRules('c_address3', {
                required: tFormLabels?.fieldRequired,
                invalid: tFormLabels?.invalidFormat,
                minLength: tFormLabels?.minLength,
                maxLength: tFormLabels?.maxLength
            }),
            error: errors[`${prefix}c_address3`],
            control
        },
        companyName: {
            name: `${prefix}companyName`,
            label:
                prefix === 'billing_'
                    ? msgf(tFormLabels?.companyNameBilling, [
                          <Box as="span" color="black" fontWeight="thin">
                              {tFormLabels?.companyNameBillingNote}
                          </Box>
                      ])
                    : msgf(tFormLabels?.companyNameShipping, [
                          <Box as="span" color="black" fontWeight="thin">
                              {tFormLabels?.companyNameShippingNote}
                          </Box>
                      ]),
            defaultValue: '',
            type: 'text',
            autoComplete: 'companyName',
            rules: createFormattedValidationRules('companyName', {
                required: tFormLabels?.fieldRequired,
                invalid: tFormLabels?.invalidFormat,
                minLength: tFormLabels?.minLength,
                maxLength: tFormLabels?.maxLength
            }),
            error: errors[`${prefix}companyName`],
            control
        },
        city: {
            name: `${prefix}city`,
            label: tFormLabels?.city,
            defaultValue: '',
            type: 'text',
            rules: createFormattedValidationRules('city', {
                required: tFormLabels?.fieldRequired,
                invalid: tFormLabels?.invalidFormat,
                minLength: tFormLabels?.minLength,
                maxLength: tFormLabels?.maxLength
            }),
            error: errors[`${prefix}city`],
            control
        },
        stateCode: {
            name: `${prefix}stateCode`,
            label: tFormLabels?.postalCode,
            defaultValue: '',
            type: 'select',
            options: [
                {value: '', label: ''},
                ...(countryCode === 'CA' ? provinceOptions : stateOptions)
            ],
            rules: {
                required:
                    countryCode === 'CA'
                        ? formatMessage({
                              defaultMessage: 'Please select your province.',
                              id: 'use_address_fields.error.please_select_your_province',
                              description:
                                  'Error message for a blank province (CA-specific checkout)'
                          })
                        : tFormLabels?.fieldRequired
            },
            error: errors[`${prefix}stateCode`],
            control
        },
        postalCode: {
            name: `${prefix}postalCode`,
            label: countryCode === 'CA' ? tFormLabels?.postalCode : tFormLabels?.zipCode,
            defaultValue: '',
            type: 'text',
            autoComplete: 'postal-code',
            rules: getPostalCodeValidationRules(countryCode, {
                required: tFormLabels?.fieldRequired,
                invalid: tFormLabels?.invalidFormat
            }),
            error: errors[`${prefix}postalCode`],
            control
        },
        preferred: {
            name: `${prefix}preferred`,
            label: tFormLabels?.preferred,
            defaultValue: false,
            type: 'checkbox',
            autoComplete: 'honorific-prefix',
            rules: {},
            control
        }
    }

    return {
        fields,
        isProgrammaticCountryChange
    }
}
